import { CellContext } from '@tanstack/react-table';
import {
    Button,
    ButtonGroup,
    DropdownMenu,
    lucideIcons,
    Popover,
    Skeleton,
    tableHelpers,
    Tooltip,
} from '@round/creatorbase-ui';
import AddCreatorToCampaignsDropdown from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/AddCreatorToCampaignsDropdown';
import { ADD_TO_CAMPAIGN_PLATFORM, useResultActions } from '../../../../hooks/useResultActions';
import { ADD_NEEDS_TAGS_HINT, getMeta, needsTagsToAdd, OutreachResultRow } from '../../../../helpers';
import styles from './ActionsCell.module.css';

const { Archive, ArchiveRestore, Check, ChevronDown, Megaphone, Plus } = lucideIcons;

const ADD_LABEL = 'Add';
const ADD_HINT = 'Add to Creator Explore';
const ADDED_LABEL = 'Added';
const ADD_TO_CAMPAIGN_LABEL = 'Add to campaign';
const ARCHIVE_LABEL = 'Archive';
const UNARCHIVE_LABEL = 'Unarchive';
const MORE_LABEL = 'More actions';

type Props = CellContext<tableHelpers.WithLoadingRow<OutreachResultRow>, unknown>;

export const ActionsCell = ({ row: { original }, table }: Props) => {
    const { isLoading } = getMeta(table);

    const {
        isAdded,
        isArchived,
        isBusy,
        isUnarchiving,
        handleAdd,
        handleArchive,
        handleUnarchive,
        isCampaignPickerOpen,
        setIsCampaignPickerOpen,
        isOpeningCampaignPicker,
        resolveAccountId,
        showPendingAdd,
    } = useResultActions(original);

    if (tableHelpers.isLoadingRow(original) || isLoading) {
        return <Skeleton className={styles.skeleton} />;
    }

    // Unarchiving is the only way on from archived — adding one is refused under `status` — so it is
    // the only thing the row offers, campaigns included.
    if (isArchived) {
        return (
            <div className={styles.clickGuard} onClick={(e) => e.stopPropagation()}>
                <ButtonGroup className={styles.actions}>
                    <Button variant="outline" size="xs" onClick={handleUnarchive} disabled={isUnarchiving}>
                        <ArchiveRestore className={styles.icon} aria-hidden="true" />
                        {UNARCHIVE_LABEL}
                    </Button>
                </ButtonGroup>
            </div>
        );
    }

    // The campaign picker adds the creator first, so it is held back on the same terms as Add.
    const isMissingTags = needsTagsToAdd(original);

    const moreActions = (
        <DropdownMenu>
            <DropdownMenu.Trigger asChild>
                <Button variant="outline" size="xs" aria-label={MORE_LABEL} disabled={isBusy}>
                    <ChevronDown className={styles.icon} aria-hidden="true" />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                align="end"
                // The menu is sized to its trigger by default, which is a chevron here.
                className={styles.menu}
                onCloseAutoFocus={(event) => {
                    if (!isOpeningCampaignPicker.current) {
                        return;
                    }

                    isOpeningCampaignPicker.current = false;
                    event.preventDefault();
                }}
            >
                <Tooltip>
                    {/* A disabled item swallows pointer events, so the trigger sits on a wrapper. */}
                    <Tooltip.Trigger asChild>
                        <div>
                            <DropdownMenu.Item
                                disabled={isMissingTags}
                                onSelect={() => {
                                    isOpeningCampaignPicker.current = true;
                                    setIsCampaignPickerOpen(true);
                                }}
                            >
                                <Megaphone aria-hidden="true" />
                                {ADD_TO_CAMPAIGN_LABEL}
                            </DropdownMenu.Item>
                        </div>
                    </Tooltip.Trigger>
                    {isMissingTags && <Tooltip.Content>{ADD_NEEDS_TAGS_HINT}</Tooltip.Content>}
                </Tooltip>
                {!isAdded && (
                    <DropdownMenu.Item onSelect={handleArchive}>
                        <Archive aria-hidden="true" />
                        {ARCHIVE_LABEL}
                    </DropdownMenu.Item>
                )}
            </DropdownMenu.Content>
        </DropdownMenu>
    );

    const actions = isAdded ? (
        <div className={styles.addedActions}>
            <span className={styles.addedStatus}>
                <Check className={styles.icon} aria-hidden="true" />
                {ADDED_LABEL}
            </span>
            {moreActions}
        </div>
    ) : (
        // Keeping is the action a row is read for; clearing one away, or spending it on a campaign,
        // is the rarer second thought — so both sit behind the menu rather than competing with Add
        // for the same glance.
        <ButtonGroup className={styles.actions}>
            <Tooltip>
                {/* A disabled button swallows pointer events, so the trigger sits on a wrapper. */}
                <Tooltip.Trigger asChild>
                    <span className={styles.addTrigger}>
                        <Button variant="outline" size="xs" onClick={handleAdd} disabled={isBusy || isMissingTags}>
                            <Plus className={styles.icon} aria-hidden="true" />
                            {ADD_LABEL}
                        </Button>
                    </span>
                </Tooltip.Trigger>
                <Tooltip.Content>{isMissingTags ? ADD_NEEDS_TAGS_HINT : ADD_HINT}</Tooltip.Content>
            </Tooltip>

            {moreActions}
        </ButtonGroup>
    );

    return (
        // The dropdown menu and the campaign picker both portal their content, so a click inside
        // either still reaches the row's onRowClick via React's tree-based bubbling even though
        // that content sits outside the row in the DOM. Stopping it here keeps an action from also
        // toggling the row's expansion.
        <div className={styles.clickGuard} onClick={(e) => e.stopPropagation()}>
            <AddCreatorToCampaignsDropdown
                accountId={original.client_influencer_account_id ?? undefined}
                resolveAccountId={resolveAccountId}
                // No saved account rate here — the result's own rate is passed as `rate` instead.
                clientInfluencer={null}
                rate={{ cost: original.cost, currencyId: original.currency_id }}
                platform={ADD_TO_CAMPAIGN_PLATFORM}
                open={isCampaignPickerOpen}
                onOpenChange={(open) => {
                    setIsCampaignPickerOpen(open);
                    // The add the picker made stands whether or not the post did, so the row catches up
                    // with it once the picker is done with the space.
                    if (!open) {
                        showPendingAdd();
                    }
                }}
                onPostCreated={showPendingAdd}
                // What opens the picker is the menu item, so the content is positioned against the
                // actions themselves rather than a trigger of its own.
                trigger={<Popover.Anchor className={styles.anchor}>{actions}</Popover.Anchor>}
            />
        </div>
    );
};
